// All Whitespaces deck content — sourced verbatim from the company document.

export const COMPANY = {
  name: "WHITESPACES",
  tagline: "Top-of-Funnel Data & Services",
  motto:
    "By fusing our 69.1M+ top-of-funnel data reservoir with advanced AI pipelines, we guarantee every impression is strategic, every click is calculated, and every campaign delivers measurable downstream growth.",
  heroLines: ["Full Funnel", "Solutions"],
  heritage: "14+ years of heritage · 1,100+ professionals",
  offices: ["Bangalore", "Delhi", "Jakarta", "Singapore", "Dubai"],
  blender:
    "Our entire infrastructure is powered by a proprietary Big Data blender — combining declared survey data, behavioral tracking and deterministic purchase signals to formulate highly accurate consumer targeting without relying on restrictive industry standards.",
};

export const DECK_PDF_URL =
  "https://customer-assets-0z36b82j.emergentagent.net/job_cohorts-experience/artifacts/53mze3pu_Cohorts-Deck%20%281%29.pdf";

export const MARQUEE_STATS = [
  "69.1M+ Leads",
  "40 Billion+ Monthly Impressions",
  "6 Million+ Clicks",
  "1.3 Billion+ Monthly Sales Events",
  "14+ Years Heritage",
  "1,100+ Professionals",
  "5 Global Offices",
];

export const HERO_STATS = [
  { value: "69.1M+", label: "Top-of-funnel leads" },
  { value: "40B+", label: "Monthly impressions" },
  { value: "1.3B+", label: "Monthly sales events" },
  { value: "1,100+", label: "Global professionals" },
];

export const PERSONAS = [
  {
    index: "01",
    name: "Fast Food Fans",
    tagline: "Immediate satisfaction. Undeniable value.",
    image:
      "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    subSegments: ["Shopping lovers", "Budget-conscious deciders", "Community-minded citizens"],
    profile:
      "Shopping lovers, budget-conscious decision-makers and highly engaged, community-minded citizens seeking immediate satisfaction and value.",
    strategy:
      "High-frequency geotargeting and Time-On-Screen optimization intercept these users during mid-day and late-night craving windows. CHIP dynamically shifts budget toward creatives showcasing the bundles and deals that historically convert budget-conscious users.",
    offerings: [
      { title: "High-Frequency Geotargeting", desc: "Location-triggered delivery during peak craving windows." },
      { title: "Time-On-Screen Optimization", desc: "Formats tuned to maximise dwell and recall." },
      { title: "Full-Screen Mobile & Header Ads", desc: "High-impact placements built to intercept in-the-moment intent." },
      { title: "CHIP Budget Shifting", desc: "Auto-routes spend to bundle & deal creatives that convert." },
    ],
    case: {
      brand: "McDonald's",
      project: "Snack & Match",
      summary:
        "Targeted budget-conscious students and fast-food lovers using zero-party survey qualifiers to identify immediate craving states across non-QSR sites.",
      metrics: [
        { value: "+39%", label: "Combo meal sales" },
        { value: "+36%", label: "Total sales uplift" },
      ],
    },
    dataApplication: {
      headline: "Localized QSR bundles at scale",
      desc:
        "Leverage the base of leads earning under 50k/month (1.5 Crore+ mapped users). Cross-reference financial data with location signals from top states like Maharashtra & UP to trigger localized QSR bundle offers during high-conversion windows.",
      stats: [
        { value: "1.5Cr+", label: "Sub-50k/mo users mapped" },
        { value: "MH · UP", label: "Priority geographies" },
      ],
    },
  },
  {
    index: "02",
    name: "Grill Masters",
    tagline: "Quality over quick fixes.",
    image:
      "https://images.unsplash.com/photo-1669707569583-8d4c8051130a?crop=entropy&cs=srgb&fm=jpg&q=90&w=2400",
    subSegments: ["Health-conscious meal planners", "Strategic shoppers", "Flavor explorers"],
    profile:
      "Health-conscious meal planners, strategic shoppers and flavor explorers who look for premium ingredients and quality over quick fixes.",
    strategy:
      "Contextual and semantic targeting queries specific lifestyle markers — weekend habits, alcohol preferences — across the open web. Rich-media video is optimised for High Viewability and VCR, focusing on brand affinity and premium lifestyle associations.",
    offerings: [
      { title: "Contextual & Semantic Targeting", desc: "Lifestyle-marker matching across the open web." },
      { title: "Rich-Media Video", desc: "Built for high viewability and completion." },
      { title: "Brand-Affinity Storytelling", desc: "Premium lifestyle associations over hard-sell." },
      { title: "VCR Optimization", desc: "Completion-rate tuned creative sequencing." },
    ],
    case: {
      brand: "Bacardi Japan",
      project: "Premium Spirits",
      summary:
        "Identified an 'Exuberant' audience — socially active, festival-goers — via Whitespaces' data hub instead of standard demographic filters.",
      metrics: [
        { value: ">65%", label: "Video completion rate" },
        { value: "+43", label: "Brand image points" },
        { value: "+17", label: "Purchase consideration" },
      ],
    },
    dataApplication: {
      headline: "Premium FMCG & spirits activation",
      desc:
        "Activate the 'Salaried' professionals (20.37%) within the 50k–100k+ income brackets. Map deterministic purchase habits to serve premium FMCG, spirits and health-conscious food to households with disposable income via high-attention formats.",
      stats: [
        { value: "20.37%", label: "Salaried professionals" },
        { value: "50k–100k+", label: "Target income band" },
      ],
    },
  },
  {
    index: "03",
    name: "Culture Lovers",
    tagline: "Investing in the exceptional.",
    image:
      "https://images.pexels.com/photos/35104078/pexels-photo-35104078.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1800",
    subSegments: ["Aesthetic curators", "Context seekers", "Lifelong learners"],
    profile:
      "Aesthetic curators, context seekers and lifelong learners who invest heavily in premium experiences, design and sophisticated asset ownership.",
    strategy:
      "Premium direct-publisher inventory with 99% verified brand safety. High-impact, non-intrusive interactive units engage the demographic, while D:TRACK measures nuanced attention metrics like in-view duration — driving deep consideration for luxury automotive and premium real estate.",
    offerings: [
      { title: "Premium Direct Inventory", desc: "Hand-picked publishers, 99% verified brand safety." },
      { title: "Interactive Ad Units", desc: "High-impact yet non-intrusive engagement." },
      { title: "D:TRACK Attention Metrics", desc: "In-view duration over simple clicks." },
      { title: "High-Ticket Consideration", desc: "Built for luxury auto & premium real estate." },
    ],
    case: {
      brand: "Dacia",
      project: "Automotive Launch",
      summary:
        "Used CHIP and proprietary machine-learning algorithms to bridge media planning and activation for high-intent car buyers.",
      metrics: [
        { value: "6x", label: "Higher precision" },
        { value: "67%", label: "Car buying intention" },
        { value: "3x", label: "Better attention rate" },
      ],
    },
    dataApplication: {
      headline: "High-ticket acquisition",
      desc:
        "Tap into the 750+ Prime Credit Score demographic. These financially stable users are targeted for high-ticket items — home loans, luxury auto — using visually immersive, high-attention formats across premium publisher networks.",
      stats: [
        { value: "37.64%", label: "Prime (750+) credit" },
        { value: "99%", label: "Verified brand safety" },
      ],
    },
  },
  {
    index: "04",
    name: "History Buffs",
    tagline: "Narrative over noise.",
    image:
      "https://images.unsplash.com/photo-1507842217343-583bb7270b66?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    subSegments: ["Story seekers", "Politics enthusiasts", "Book lovers"],
    profile:
      "Story seekers, politics enthusiasts and book lovers. Analytical, highly observant, and drawn to narratives and deep-dive content.",
    strategy:
      "The 'Ad Chooser' interactive format lets users choose their preferred ad experience — selecting between two video storylines. Active participation drives radically higher ad recall and completion, perfectly matching their desire for contextual depth.",
    offerings: [
      { title: "'Ad Chooser' Format", desc: "Viewers select their preferred storyline." },
      { title: "Long-Form Narrative", desc: "Deep-dive, interactive brand storytelling." },
      { title: "Creative A/B Storylines", desc: "Reveals which narrative drives recall." },
      { title: "Recall-Led Measurement", desc: "Optimised for memory, not just impressions." },
    ],
    case: {
      brand: "Cadbury",
      project: "Heroes League",
      summary:
        "Deployed the 'Ad Chooser' format, letting story-focused users select between two video creatives and handing them control over the narrative.",
      metrics: [
        { value: "87.8%", label: "Visible VCR" },
        { value: "+5.6%", label: "Higher ad recall" },
      ],
    },
    dataApplication: {
      headline: "Declarative narrative research",
      desc:
        "Deploy declarative surveys against the highly concentrated 26–45 age groups. Understanding their political, literary and storytelling preferences maps content consumption to deliver long-form, interactive brand narratives.",
      stats: [
        { value: "26–45", label: "Core age concentration" },
        { value: "86.7L", label: "Leads in age band" },
      ],
    },
  },
  {
    index: "05",
    name: "The Gamers",
    tagline: "Ad-blind. Hyper-engaged. Always in the game.",
    image:
      "https://images.unsplash.com/photo-1592424002053-21f369ad7fdb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    subSegments: ["Mobile gaming natives", "PC & console enthusiasts", "Esports spectators"],
    profile:
      "High-engagement digital natives — mobile, console and PC gaming enthusiasts who invest heavily in premium digital experiences and esports, and tune out anything that isn't native to the game.",
    strategy:
      "D:TRACK maps 4+ hrs/day mobile gaming sessions to isolate peak-engagement windows, while CHIP auto-shifts budget toward in-game native placements, esports sponsorship overlays and rewarded video — the only formats this notoriously ad-blind cohort tolerates.",
    offerings: [
      { title: "In-Game Native Ads", desc: "Placements built into the play, not on top of it." },
      { title: "Esports Sponsorship Overlay", desc: "Branded presence inside live competitive streams." },
      { title: "Rewarded Video", desc: "Value-exchange formats gamers opt into mid-session." },
      { title: "CHIP Budget Shifting", desc: "Auto-routes spend to peak session-depth moments." },
    ],
    case: {
      brand: "Konami",
      project: "Building Pre-Order Hype",
      summary:
        "Built pre-launch hype and drove pre-orders among India's mobile and PC gaming cohort — a notoriously ad-blind audience with zero tolerance for irrelevant placements.",
      metrics: [
        { value: "3.2x", label: "Engagement rate" },
        { value: "68%+", label: "In-game click rate" },
        { value: "45%+", label: "Sub conversions" },
      ],
    },
    dataApplication: {
      headline: "Peak-session gaming activation",
      desc:
        "Isolate the 4+ hrs/day mobile gaming segment (age 16–30) via CAUSECT's open-web intent capture. Cross-reference esports affinity and digital-goods spend to trigger native in-game placements at the exact session-depth moment engagement peaks.",
      stats: [
        { value: "4+ hrs/day", label: "Mobile gaming sessions" },
        { value: "16–30", label: "Core age band" },
      ],
    },
  },
  {
    index: "06",
    name: "Heroes League",
    tagline: "Four hours a day isn't a binge. It's a habit.",
    image:
      "https://images.unsplash.com/photo-1641232458416-feace752b346?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    subSegments: ["Heavy OTT streamers", "Regional-language loyalists", "Live-sports followers"],
    profile:
      "Heavy OTT consumers who watch 4+ hours daily, following regional-language content and live sporting events closely across premium streaming platforms.",
    strategy:
      "D:TRACK isolates genuine streaming intent from casual browsing, while CHIP routes budget to pre-roll and mid-roll inventory around blockbuster originals — driving high-frequency retargeting toward subscription conversion at scale.",
    offerings: [
      { title: "Blockbuster Pre-Roll Placement", desc: "Premium inventory around originals & tentpole releases." },
      { title: "Regional Language Targeting", desc: "Content-language matched to viewer affinity." },
      { title: "High-Frequency Retargeting", desc: "Repeated touches through the binge window." },
      { title: "Data Reservoir Enrichment", desc: "Streaming intent isolated from casual browsing." },
    ],
    case: {
      brand: "JioCinema",
      project: "Blockbuster Originals: Subscriber Growth",
      summary:
        "Converted heavy OTT consumers into paying subscribers at scale — particularly the regional-language audience segment that competitor platforms had failed to activate efficiently.",
      metrics: [
        { value: "2.5B+", label: "Total engagements" },
        { value: "4.8x", label: "Subscriber conversion" },
        { value: "62%", label: "Regional language uplift" },
      ],
    },
    dataApplication: {
      headline: "Regional-language subscriber growth",
      desc:
        "Map the 18–40 age band watching 4+ hrs/day of OTT against regional-language affinity and sports-viewership signals from the Data Reservoir. Deploy premium blockbuster placements timed to peak binge windows to convert casual viewers into paying subscribers.",
      stats: [
        { value: "18–40", label: "Core age band" },
        { value: "62%", label: "Regional uplift" },
      ],
    },
  },
];

export const BRANDS = [
  { name: "Bacardi", slug: "bacardi", logo: "/logos/bacardi.png", personaIndex: "02" },
  { name: "Big Bazaar", slug: "bigbazaar", logo: "/logos/bigbazaar.png" },
  { name: "BookMyShow", slug: "bookmyshow", logo: "/logos/bookmyshow.png" },
  { name: "Brigade Group", slug: "brigade", logo: "/logos/brigade.png" },
  { name: "Cadbury", slug: "cadbury", logo: "/logos/cadbury.png", personaIndex: "04" },
  { name: "Dacia", slug: "dacia", logo: "/logos/dacia.png", personaIndex: "03" },
  { name: "Fujifilm", slug: "fujifilm", logo: "/logos/fujifilm.png" },
  { name: "Hoichoi", slug: "hoichoi", logo: "/logos/hoichoi.png" },
  { name: "IFBA", slug: "ifba", logo: "/logos/ifba.png" },
  { name: "JioCinema", slug: "jiocinema", logo: "/logos/jiocinema.png", personaIndex: "06" },
  { name: "Konami", slug: "konami", logo: "/logos/konami.png", personaIndex: "05" },
  { name: "Lodha Group", slug: "lodha", logo: "/logos/lodha.png" },
  { name: "McDonald's", slug: "mcdonalds", logo: "/logos/mcdonalds.png", personaIndex: "01" },
  { name: "Skillbox", slug: "skillbox", logo: "/logos/skillbox.png" },
  { name: "Tanishq", slug: "tanishq", logo: "/logos/tanishq.png" },
  { name: "Urban Company", slug: "urbancompany", logo: "/logos/urbancompany.png" },
];

export const TECH = [
  {
    code: "CAUSECT",
    title: "Web Trail Analyzer",
    short: "Real-time analysis of consumer digital footprints.",
    detail:
      "A real-time web trail analyzer that deep-dives into consumer digital footprints across the open web — reconstructing the path, context and intent behind every session.",
    tags: ["Real-time", "Footprint mapping", "Open-web"],
  },
  {
    code: "RTNLZE",
    title: "Data Intelligence",
    short: "Enriches legacy research into actionable insight.",
    detail:
      "Rationalize is a data-intelligence application that enriches legacy research data, turning static data points into actionable insights ready for activation.",
    tags: ["Enrichment", "Legacy research", "Activation-ready"],
  },
  {
    code: "D:TRACK",
    title: "Recommendation Engine",
    short: "Maps live KPIs against consumer footprints.",
    detail:
      "An algorithmic recommendation engine that tracks digital campaign KPIs and maps them against consumer footprints for continuous real-time optimization.",
    tags: ["KPI tracking", "Attention metrics", "Real-time optimization"],
  },
  {
    code: "CAUSE",
    title: "Signal Visualizer",
    short: "Unifies structured & unstructured signals.",
    detail:
      "A centralized platform that visualizes structured and unstructured search, social and rating data through customized analytics plugins — one canvas for every signal.",
    tags: ["Search", "Social", "Ratings", "Custom plugins"],
  },
  {
    code: "CHIP",
    title: "Real-Time Narrative AI",
    short: "Neural pipeline testing up to 18 variables.",
    detail:
      "A neural-network-driven pipeline that tests up to 18 variables over a 3-phase optimization cycle to predict clicks and issue automated budget recommendations.",
    tags: ["Neural network", "18 variables", "3-phase cycle", "Auto-budget"],
    variables: ["Bounce rate", "Time on screen", "Social actions", "+ up to 15 more signals"],
    phases: [
      { n: "01", label: "Learn", desc: "Ingests live signals to model audience behavior." },
      { n: "02", label: "Predict", desc: "Forecasts click propensity across creatives." },
      { n: "03", label: "Allocate", desc: "Issues automated budget recommendations." },
    ],
    featured: true,
  },
];

export const DATABASE = {
  total: "6,91,33,926",
  totalShort: "69.1M+",
  totalLabel: "Acquired Leads",
  attributes: {
    gender: {
      label: "Gender",
      unit: "%",
      note: "Male 5,01,56,663 · Female 1,89,77,263",
      data: [
        { name: "Male", value: 72.55 },
        { name: "Female", value: 27.45 },
      ],
    },
    age: {
      label: "Age Band",
      unit: "L",
      note: "Concentrated in the 26–45 prime-spending years",
      data: [
        { name: "26–35", value: 55.8 },
        { name: "36–45", value: 30.9 },
      ],
    },
    salary: {
      label: "Monthly Salary",
      unit: "L",
      note: "Deep value base, premium tier for high-ticket",
      data: [
        { name: "25k–50k", value: 102 },
        { name: "50k–100k", value: 29 },
        { name: "100k+", value: 11.4 },
      ],
    },
    credit: {
      label: "Credit Score",
      unit: "%",
      note: "37.64% sit in the Prime (750+) band",
      data: [
        { name: "750+ (Prime)", value: 37.64 },
        { name: "650–750", value: 27.22 },
        { name: "Below 650", value: 35.14 },
      ],
    },
    profession: {
      label: "Profession",
      unit: "%",
      note: "Salaried & self-employed with disposable income",
      data: [
        { name: "Others", value: 70.38 },
        { name: "Salaried", value: 20.37 },
        { name: "Self-Employed", value: 8.83 },
      ],
    },
    geography: {
      label: "Geography",
      unit: "%",
      note: "Top states drive localized activation",
      data: [
        { name: "Maharashtra", value: 18.4 },
        { name: "Uttar Pradesh", value: 15.2 },
        { name: "Karnataka", value: 11.7 },
        { name: "Rest of India", value: 54.7 },
      ],
    },
  },
};
